/**
 * Decoder Keyboard Shortcuts Hook
 * Handles global keyboard shortcuts for decoder
 */

import { useEffect } from 'react';
import { useDecoder } from './useDecoder';
import { useDecoderStore } from '../store/decoder-store';
import { useBottomPanelStore } from '../store/bottomPanelStore';

export function useDecoderKeyboard() {
  const { openDecoder, openDecoderWith } = useDecoder();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Skip if event was already handled by another handler
      if (event.defaultPrevented) return;

      const isModKey = event.metaKey || event.ctrlKey;

      // Read state directly from stores to always get fresh values
      const isBottomPanelOpen = useBottomPanelStore.getState().isOpen;
      const selectedBottomPanelTab = useBottomPanelStore.getState().selectedTab;
      const isDecoderOpen =
        isBottomPanelOpen && selectedBottomPanelTab === 'decoder';

      // Cmd+D (Mac) or Ctrl+D (Windows/Linux) - Toggle Decoder
      if (isModKey && event.key === 'd') {
        event.preventDefault();

        // If decoder is already open, close it
        if (isDecoderOpen) {
          useBottomPanelStore.getState().close();
          return;
        }

        // Try to get selected text
        const selection = window.getSelection();
        const selectedText = selection?.toString().trim();

        if (selectedText) {
          // Open decoder with selected text
          openDecoderWith(selectedText);
        } else {
          // Just open decoder
          openDecoder();
        }
      }

      // Only handle these shortcuts when decoder is open
      if (!isDecoderOpen) return;

      // Escape - Close Decoder
      if (event.key === 'Escape') {
        event.preventDefault();
        useBottomPanelStore.getState().close();
      }

      // Cmd+K - Clear Input
      if (isModKey && event.key === 'k') {
        event.preventDefault();
        useDecoderStore.getState().setInitialInput('');
        useDecoderStore.getState().clearAllSteps();
      }

      // Cmd+Enter - Apply Operation (add first step if none exist)
      if (isModKey && event.key === 'Enter') {
        event.preventDefault();
        const { initialInput, steps, addStep, selectedOperation } =
          useDecoderStore.getState();
        if (initialInput && steps.length === 0) {
          addStep({ operation: selectedOperation });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [openDecoder, openDecoderWith]);
}
